import { lazy, Suspense } from 'react';
import { Outlet, useParams, Navigate } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
import { RightsGuard } from 'src/auth/guard/rights-guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
import FeatureMasterPage from 'src/pages/dashboard/utils/feature-master';
import HolidaySimplePage from 'src/pages/dashboard/jurisdiction/HolidaySimplePage';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/pages/dashboard/app'));

const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));

const SiteSettingsPage = lazy(() => import('src/pages/dashboard/site-settings/settings'));

// Admin & Users
const AdminUserPage = lazy(() => import('src/pages/dashboard/admin-users/AdminUserPage'));
const UserPage = lazy(() => import('src/pages/dashboard/users/UserPage'));
const OrganizationPage = lazy(() => import('src/pages/dashboard/organizations/OrganizationPage'));
const AppUserPage = lazy(() => import('src/pages/dashboard/app-users/AppUserPage'));

// Jurisdiction Pages
const CountryMasterPage = lazy(() => import('src/pages/dashboard/jurisdiction/country-master'));
const StateMasterPage = lazy(() => import('src/pages/dashboard/jurisdiction/state-master'));
const CityMasterPage = lazy(() => import('src/pages/dashboard/jurisdiction/city-master'));
const BlockMasterPage = lazy(() => import('src/pages/dashboard/jurisdiction/block-master'));
const LocalityMasterPage = lazy(() => import('src/pages/dashboard/jurisdiction/locality-master'));
const ClusterMasterPage = lazy(() => import('src/pages/dashboard/jurisdiction/cluster-master'));
const HolidayCalendarPage = lazy(() =>
  import('src/pages/dashboard/jurisdiction/HolidayCalendarPage')
);

// Utils/Masters Pages
const ModuleMasterPage = lazy(() => import('src/pages/dashboard/utils/module-master'));
const MenuMasterPage = lazy(() => import('src/pages/dashboard/utils/menu-master'));
const SubmenuMasterPage = lazy(() => import('src/pages/dashboard/utils/submenu-master'));
const MenuStructurePage = lazy(() => import('src/pages/dashboard/menu-structure'));

// HRM Pages
const DesignationMasterPage = lazy(() => import('src/pages/dashboard/hrm/designation-master'));
const DepartmentMasterPage = lazy(() => import('src/pages/dashboard/hrm/department-master'));
const KRAMasterPage = lazy(() => import('src/pages/dashboard/performance/kra-master'));
const MyTasksPage = lazy(() => import('src/pages/dashboard/performance/my-tasks'));
const ExpenseCategoryPage = lazy(() => import('src/sections/expense-category/ExpenseCategoryPage'));
const ManageExpensePage = lazy(() => import('src/sections/manage-expense/ManageExpensePage'));
const ExpenseApprovalPage = lazy(() =>
  import('src/pages/dashboard/expense-approval/expense-approval')
);
const LeaveTypeMasterPage = lazy(() => import('src/pages/dashboard/leave-type-master'));
const LeaveApplicationPage = lazy(() => import('src/pages/dashboard/leave-application'));
const LeaveApprovalPage = lazy(() => import('src/pages/dashboard/leave-approval/leave-approval'));
const LeaveAnalyticsPage = lazy(() =>
  import('src/pages/dashboard/leave-analytics/LeaveAnalyticsPage')
);
const LeaveEncashmentPage = lazy(() =>
  import('src/pages/dashboard/leave-encashment/LeaveEncashmentPage')
);
const LeaveBalancePage = lazy(() =>
  import('src/pages/dashboard/leave-balances/LeaveBalancePage')
);
const ResignationListPage = lazy(() => import('src/sections/resignation/ResignationListView'));
const ResignationApprovalPage = lazy(() =>
  import('src/sections/resignation/ResignationApprovalView')
);

// Document Type Master & Document Request Pages
const DocumentTypeMasterPage = lazy(() =>
  import('src/pages/dashboard/document-type-master/DocumentTypeMasterPage')
);
const DocumentRequestPage = lazy(() =>
  import('src/pages/dashboard/document-request/DocumentRequestPage')
);
const DocumentRequestApprovalPage = lazy(() =>
  import('src/pages/dashboard/document-request/DocumentRequestApprovalPage')
);
const UserGroupPage = lazy(() => import('src/pages/dashboard/hrm/user-group'));
const UserRightsPage = lazy(() => import('src/pages/dashboard/hrm/user-rights'));
const UnifiedCalendarPage = lazy(() => import('src/pages/dashboard/hrm/UnifiedCalendarPage'));
const StaffMigrationPage = lazy(() => import('src/pages/dashboard/hrm/staff-migrations'));
const ReportingHierarchyPage = lazy(() => import('src/pages/dashboard/hrm/reporting-hierarchy'));
const GeographyUserRightsView = lazy(() =>
  import('src/sections/geography-rights/GeographyUserRightsView')
);
const ProjectsPage = lazy(() => import('src/pages/dashboard/projects/projects'));
const ProjectAssignPage = lazy(() => import('src/pages/dashboard/projects/project-assign'));
const DataCollectionPage = lazy(() => import('src/sections/data-collection/DataCollectionPage'));
const ProjectFormsPage = lazy(() => import('src/sections/data-collection/ProjectFormsPage'));
const FormBuilderPage = lazy(() => import('src/sections/data-collection/FormBuilderPage'));
const FormDataListPage = lazy(() => import('src/sections/data-collection/FormDataListPage'));
const FormSubmissionPage = lazy(() => import('src/sections/data-collection/FormSubmissionPage'));
const FormAnalyticsPage = lazy(() => import('src/sections/data-collection/FormAnalyticsPage'));

// Plan Master Page
const PlanMasterPage = lazy(() => import('src/pages/dashboard/plan-master/plan-master'));

// Storage Master Page
const StorageMasterPage = lazy(() => import('src/pages/dashboard/storage-master/storage-master'));

// Attendance Pages
const AttendanceView = lazy(() => import('src/pages/dashboard/attendance/AttendanceView'));
const AttendanceApprovalView = lazy(() =>
  import('src/pages/dashboard/attendance/AttendanceApprovalView')
);
const ForgetPunchApprovalPage = lazy(() =>
  import('src/pages/dashboard/attendance/forget-punch-approval')
);
const ForgetPunchRequestPage = lazy(() =>
  import('src/pages/dashboard/attendance/forget-punch-request')
);
const MonthlyAttendanceReportsView = lazy(() =>
  import('src/pages/dashboard/reports/MonthlyAttendanceReportsView')
);

// Festival Page
const FestivalMasterPage = lazy(() => import('src/pages/dashboard/festivals/festival'));

// Template Requests Page
const TemplateRequestsPage = lazy(() =>
  import('src/pages/dashboard/template-requests/template-requests')
);

// Storage Dashboard Page
const StorageDashboardView = lazy(() => import('src/sections/storage/StorageDashboardView'));

// Drive Page
const DriveManager = lazy(() => import('src/sections/drive/DriveManager'));

// Travel Management Pages
const VehicleTypeMasterPage = lazy(() =>
  import('src/pages/dashboard/travel-management/vehicle-type-master')
);
const VehicleMileagePage = lazy(() =>
  import('src/pages/dashboard/travel-management/vehicle-mileage')
);
const DailyPlannerPage = lazy(() => import('src/pages/dashboard/hrm/daily-planner'));
const GeoFencingPage = lazy(() => import('src/pages/dashboard/hrm/geo-fencing'));
const AnnouncementPage = lazy(() => import('src/pages/dashboard/hrm/announcement'));

// Chat Page
const ChatViewPage = lazy(() => import('src/sections/chat/view/chat-view'));

// Dashboard Designer & Viewer Pages
const DashboardDesignerPage = lazy(() =>
  import('../../sections/dashboard/designer/DashboardDesignerPage')
);
const AnalyticsDashboardListPage = lazy(() =>
  import('../../sections/dashboard/designer/AnalyticsDashboardListPage')
);

// Website Builder Pages
const WebsiteListPage = lazy(() => import('../../sections/website-builder/WebsiteListPage'));
const WebsiteEditorPage = lazy(() => import('../../sections/website-builder/WebsiteEditorPage'));

// FieldStudio Pages
const DocumentCreatorListPage = lazy(() =>
  import('../../sections/fieldstudio/DocumentCreatorListPage')
);
const DocumentEditorPage = lazy(() => import('../../sections/fieldstudio/DocumentEditorPage'));
const DocumentTemplateManagerPage = lazy(() =>
  import('../../sections/fieldstudio/DocumentTemplateManagerPage')
);
const WebsiteAnalyticsPage = lazy(() =>
  import('../../sections/website-builder/WebsiteAnalyticsPage')
);
const TemplateGalleryPage = lazy(() => import('../../sections/fieldstudio/TemplateGalleryPage'));
const TemplateToEditorLoader = lazy(() =>
  import('../../sections/fieldstudio/TemplateToEditorLoader')
);
const TemplateMarketplacePage = lazy(() =>
  import('../../sections/fieldstudio/TemplateMarketplacePage')
);

// Kiosk Management Page
const KioskListPage = lazy(() => import('src/pages/dashboard/kiosk/list'));

// Hiring Management Pages
const HiringJobsPage = lazy(() => import('src/pages/dashboard/hiring/HiringJobsPage'));
const HiringApplicationsPage = lazy(() => import('src/pages/dashboard/hiring/HiringApplicationsPage'));
const HiringPipelinePage = lazy(() => import('src/pages/dashboard/hiring/HiringPipelinePage'));
const HiringInterviewsPage = lazy(() => import('src/pages/dashboard/hiring/HiringInterviewsPage'));
const HiringSettingsPage = lazy(() => import('src/pages/dashboard/hiring/HiringSettingsPage'));
const HiringAnalyticsPage = lazy(() => import('src/pages/dashboard/hiring/HiringAnalyticsPage'));
const HiringTalentPoolPage = lazy(() => import('src/pages/dashboard/hiring/HiringTalentPoolPage'));
const HiringReferralsPage = lazy(() => import('src/pages/dashboard/hiring/HiringReferralsPage'));

// ----------------------------------------------------------------------

// Onboarding
const OnboardingPage = lazy(() => import('src/pages/dashboard/onboarding'));

// Payroll Pages
const PayrollListView = lazy(() => import('src/sections/payroll/view/PayrollListView'));
const PayrollDetailView = lazy(() => import('src/sections/payroll/view/PayrollDetailView'));
const MyPayslipsView = lazy(() => import('src/sections/payroll/view/MyPayslipsView'));
const EmployeeSalaryListView = lazy(() =>
  import('src/sections/payroll/view/EmployeeSalaryListView')
);

// WhatsApp Platform
const WhatsAppLogPage = lazy(() => import('src/pages/dashboard/whatsapp-logs/WhatsAppLogPage'));
const WhatsAppAnalyticsPage = lazy(() =>
  import('src/sections/whatsapp/view/WhatsAppAnalyticsView')
);
const WhatsAppCampaignsPage = lazy(() =>
  import('src/sections/whatsapp/view/WhatsAppCampaignsView')
);
const WhatsAppInboxPage = lazy(() => import('src/sections/whatsapp/view/WhatsAppInboxView'));
const WhatsAppTemplatesPage = lazy(() =>
  import('src/sections/whatsapp/view/WhatsAppTemplatesView')
);
const WhatsAppSequencesPage = lazy(() =>
  import('src/sections/whatsapp/view/WhatsAppSequencesView')
);
const WhatsAppBotFAQPage = lazy(() => import('src/sections/whatsapp/view/WhatsAppBotFAQView'));
const WhatsAppContactsPage = lazy(() => import('src/sections/whatsapp/view/WhatsAppContactsView'));
const WhatsAppKnowledgePage = lazy(() =>
  import('src/sections/whatsapp/view/WhatsAppKnowledgeView')
);
const WhatsAppFlowsPage = lazy(() => import('src/sections/whatsapp/view/WhatsAppFlowsView'));
const WhatsAppFlowBuilderPage = lazy(() =>
  import('src/sections/whatsapp/view/WhatsAppFlowBuilderView')
);
const WhatsAppBotFlowManagerPage = lazy(() =>
  import('src/sections/whatsapp/view/WhatsAppBotFlowManagerView')
);
const WhatsAppSettingsPage = lazy(() => import('src/sections/whatsapp/view/WhatsAppSettingsView'));
const WhatsAppOrgBotManagerPage = lazy(() =>
  import('src/pages/dashboard/whatsapp-bot-manager/WhatsAppOrgBotManagerPage')
);

// Assets Management Pages
const AssetTypesPage = lazy(() => import('src/pages/dashboard/assets/types'));
const AssetDevicesPage = lazy(() => import('src/pages/dashboard/assets/devices'));
const AssetRegistryPage = lazy(() => import('src/pages/dashboard/assets/registry'));
const MyActiveAssetsPage = lazy(() => import('src/pages/dashboard/assets/my-active'));
const RequestAssetPage = lazy(() => import('src/pages/dashboard/assets/request-asset'));
const AllAssetRequestsPage = lazy(() => import('src/pages/dashboard/assets/requests-approval'));
const AssetAllocationsPage = lazy(() => import('src/pages/dashboard/assets/allocations'));
const SuppliersPage = lazy(() => import('src/pages/dashboard/assets/suppliers'));
const AssetStockPage = lazy(() => import('src/pages/dashboard/assets/stock'));

// ----------------------------------------------------------------------

// Dashboard layout wrapper for all routes
const DashboardWrapper = (
  <AuthGuard>
    <DashboardLayout>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    </DashboardLayout>
  </AuthGuard>
);

const ROUTE_MAP = {
  // Assets Management
  asset_types: {
    component: <AssetTypesPage />,
    guardConfig: { submenu: 'asset_types' },
  },
  asset_devices: {
    component: <AssetDevicesPage />,
    guardConfig: { submenu: 'asset_devices' },
  },
  asset_registry: {
    component: <AssetRegistryPage />,
    guardConfig: { submenu: 'asset_registry' },
  },
  my_active_assets: {
    component: <MyActiveAssetsPage />,
    guardConfig: { submenu: 'my_active_assets' },
  },
  request_asset: {
    component: <RequestAssetPage />,
    guardConfig: { submenu: 'request_asset' },
  },
  all_asset_requests: {
    component: <AllAssetRequestsPage />,
    guardConfig: { submenu: 'all_asset_requests' },
  },
  asset_allocations: {
    component: <AssetAllocationsPage />,
    guardConfig: { submenu: 'asset_allocations' },
  },
  asset_suppliers: {
    component: <SuppliersPage />,
    guardConfig: { submenu: 'asset_suppliers' },
  },
  asset_stock: {
    component: <AssetStockPage />,
    guardConfig: { submenu: 'asset_stock' },
  },

  // HRM / User Management
  attendance: {
    component: <AttendanceView />,
    guardConfig: { skipRightsCheck: true },
  },
  mark: {
    component: <AttendanceView />,
    guardConfig: { skipRightsCheck: true },
  },
  apply: {
    component: <ResignationListPage />,
    guardConfig: { skipRightsCheck: true },
  },
  forget_punch: {
    component: <ForgetPunchRequestPage />,
    guardConfig: { skipRightsCheck: true, featureFlag: 'forgetAllowanceEnabled' },
  },
  forget_punch_approval: {
    component: <ForgetPunchApprovalPage />,
    guardConfig: { submenu: 'forget_punch_approval', feature: 'view_all' },
  },
  user_list: {
    component: <UserPage />,
    guardConfig: { submenu: 'user_list' },
  },
  users: {
    component: <UserPage />,
    guardConfig: { submenu: 'users' },
  },
  designation: {
    component: <DesignationMasterPage />,
    guardConfig: { submenu: 'designation' },
  },
  department: {
    component: <DepartmentMasterPage />,
    guardConfig: { submenu: 'department' },
  },
  kra_master: {
    component: <KRAMasterPage />,
    guardConfig: { menu: 'kra_master' },
  },
  kar_master: {
    component: <KRAMasterPage />,
    guardConfig: { menu: 'kra_master' },
  },
  my_tasks: {
    component: <MyTasksPage />,
    guardConfig: { menu: 'my_tasks' },
  },
  'my-tasks': {
    component: <MyTasksPage />,
    guardConfig: { menu: 'my_tasks' },
  },
  user_groups: {
    component: <UserGroupPage />,
    guardConfig: { submenu: 'user_groups' },
  },
  user_rights: {
    component: <UserRightsPage />,
    guardConfig: { submenu: 'user_rights', feature: 'view_all' },
  },
  attendance_approval: {
    component: <AttendanceApprovalView />,
    guardConfig: { submenu: 'attendance_approval', feature: 'view_all' },
  },
  calendar: {
    component: <UnifiedCalendarPage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },
  geography_rights: {
    component: <GeographyUserRightsView />,
    guardConfig: { requiredRole: 'admin' },
  },
  resignation_approval: {
    component: <ResignationApprovalPage />,
    guardConfig: { submenu: 'resignation_approval', feature: 'view_all' },
  },
  staff_migrations: {
    component: <StaffMigrationPage />,
    guardConfig: { submenu: 'staff_migrations', feature: 'view_all' },
  },
  'staff-migrations': {
    component: <StaffMigrationPage />,
    guardConfig: { submenu: 'staff_migrations', feature: 'view_all' },
  },
  reporting_hierarchy: {
    component: <ReportingHierarchyPage />,
    guardConfig: { submenu: 'reporting_hierarchy' },
  },
  'reporting-hierarchy': {
    component: <ReportingHierarchyPage />,
    guardConfig: { submenu: 'reporting_hierarchy' },
  },
  holiday_calendar: {
    component: <HolidaySimplePage />,
    guardConfig: { skipRightsCheck: true },
  },
  holiday: {
    component: <HolidayCalendarPage />,
    guardConfig: {},
  },
  holiday_managment: {
    component: <HolidayCalendarPage />,
    guardConfig: {},
  },
  holiday_management: {
    component: <HolidayCalendarPage />,
    guardConfig: {},
  },
  planner: {
    component: <DailyPlannerPage />,
    guardConfig: { excludeRoles: ['super_admin'] },
  },
  geo_fencing: {
    component: <GeoFencingPage />,
    guardConfig: { excludeRoles: ['super_admin'] },
  },
  resignation: {
    component: <ResignationListPage />,
    guardConfig: { skipRightsCheck: true },
  },
  announcement: {
    component: <AnnouncementPage />,
    guardConfig: { excludeRoles: ['super_admin'] },
  },

  // Documents
  document_type: {
    component: <DocumentTypeMasterPage />,
    guardConfig: {},
  },
  document_requests: {
    component: <DocumentRequestPage />,
    guardConfig: { skipRightsCheck: true },
  },
  document_request_approvals: {
    component: <DocumentRequestApprovalPage />,
    guardConfig: {},
  },

  // Kiosk
  kiosk: {
    component: <KioskListPage />,
    guardConfig: { skipRightsCheck: true },
  },
  kiosk_devices: {
    component: <KioskListPage />,
    guardConfig: { skipRightsCheck: true },
  },
  kiosk_visitors: {
    component: <KioskListPage />,
    guardConfig: { skipRightsCheck: true },
  },
  kiosk_interviews: {
    component: <KioskListPage />,
    guardConfig: { skipRightsCheck: true },
  },
  kiosk_theme: {
    component: <KioskListPage />,
    guardConfig: { skipRightsCheck: true },
  },

  // Leave Management
  leave_types: {
    component: <LeaveTypeMasterPage />,
    guardConfig: {},
  },
  leave_applications: {
    component: <LeaveApplicationPage />,
    guardConfig: { skipRightsCheck: true },
  },
  leave_approvals: {
    component: <LeaveApprovalPage />,
    guardConfig: {},
  },
  leave_analytics: {
    component: <LeaveAnalyticsPage />,
    guardConfig: {},
  },
  leave_encashment: {
    component: <LeaveEncashmentPage />,
    guardConfig: { skipRightsCheck: true },
  },

  // Payroll
  payroll: {
    component: <PayrollListView />,
    guardConfig: { noGuard: true },
  },
  my_payslips: {
    component: <MyPayslipsView />,
    guardConfig: { noGuard: true },
  },
  employee_salary: {
    component: <EmployeeSalaryListView />,
    guardConfig: { noGuard: true },
  },

  // Reports
  monthly_attendance: {
    component: <MonthlyAttendanceReportsView />,
    guardConfig: { excludeRoles: ['super_admin'] },
  },

  // Master / Jurisdiction
  country: {
    component: <CountryMasterPage />,
    guardConfig: { submenu: 'country' },
  },
  state: {
    component: <StateMasterPage />,
    guardConfig: { submenu: 'state' },
  },
  city: {
    component: <CityMasterPage />,
    guardConfig: { submenu: 'city' },
  },
  cities: {
    component: <CityMasterPage />,
    guardConfig: { submenu: 'city' },
  },
  block: {
    component: <BlockMasterPage />,
    guardConfig: { submenu: 'block' },
  },
  blocks: {
    component: <BlockMasterPage />,
    guardConfig: { submenu: 'block' },
  },
  locality: {
    component: <LocalityMasterPage />,
    guardConfig: { submenu: 'locality' },
  },
  localities: {
    component: <LocalityMasterPage />,
    guardConfig: { submenu: 'locality' },
  },
  cluster: {
    component: <ClusterMasterPage />,
    guardConfig: { submenu: 'cluster' },
  },
  // Master / Menu Management
  module: {
    component: <ModuleMasterPage />,
    guardConfig: { submenu: 'module_master' },
  },
  module_master: {
    component: <ModuleMasterPage />,
    guardConfig: { submenu: 'module_master' },
  },
  menu_master: {
    component: <MenuMasterPage />,
    guardConfig: { submenu: 'menu_master' },
  },
  submenu: {
    component: <SubmenuMasterPage />,
    guardConfig: { submenu: 'submenu_master' },
  },
  submenu_master: {
    component: <SubmenuMasterPage />,
    guardConfig: { submenu: 'submenu_master' },
  },
  feature: {
    component: <FeatureMasterPage />,
    guardConfig: { submenu: 'feature_master' },
  },
  feature_master: {
    component: <FeatureMasterPage />,
    guardConfig: { submenu: 'feature_master' },
  },

  // Settings
  site_settings: {
    component: <SiteSettingsPage />,
    guardConfig: {},
  },
  'site-settings': {
    component: <SiteSettingsPage />,
    guardConfig: {},
  },
  whatsapp_logs: {
    component: <WhatsAppLogPage />,
    guardConfig: { skipRightsCheck: true },
  },

  // WhatsApp
  analytics: {
    component: <WhatsAppAnalyticsPage />,
    guardConfig: { skipRightsCheck: true },
  },
  inbox: {
    component: <WhatsAppInboxPage />,
    guardConfig: { skipRightsCheck: true },
  },
  campaigns: {
    component: <WhatsAppCampaignsPage />,
    guardConfig: { skipRightsCheck: true },
  },
  whatsapp_templates: {
    component: <WhatsAppTemplatesPage />,
    guardConfig: { skipRightsCheck: true },
  },
  sequences: {
    component: <WhatsAppSequencesPage />,
    guardConfig: { skipRightsCheck: true },
  },
  'bot-faqs': {
    component: <WhatsAppBotFAQPage />,
    guardConfig: { skipRightsCheck: true },
  },
  logs: {
    component: <WhatsAppLogPage />,
    guardConfig: { skipRightsCheck: true },
  },
  contacts: {
    component: <WhatsAppContactsPage />,
    guardConfig: { skipRightsCheck: true },
  },
  'knowledge-base': {
    component: <WhatsAppKnowledgePage />,
    guardConfig: { skipRightsCheck: true },
  },
  flows: {
    component: <WhatsAppFlowsPage />,
    guardConfig: { skipRightsCheck: true },
  },
  'bot-flow-manager': {
    component: <WhatsAppBotFlowManagerPage />,
    guardConfig: { requiredRole: 'super_admin', skipRightsCheck: true },
  },
  settings: {
    component: <WhatsAppSettingsPage />,
    guardConfig: { skipRightsCheck: true },
  },

  // Festivals
  festival: {
    component: <FestivalMasterPage />,
    guardConfig: {},
  },

  // Expense
  expense_category: {
    component: <ExpenseCategoryPage />,
    guardConfig: {},
  },
  manage_expense: {
    component: <ManageExpensePage />,
    guardConfig: {},
  },
  expense_approvals: {
    component: <ExpenseApprovalPage />,
    guardConfig: {},
  },

  // Super Admin
  admin_users: {
    component: <AdminUserPage />,
    guardConfig: { requiredRole: 'super_admin' },
  },
  'admin-users': {
    component: <AdminUserPage />,
    guardConfig: { requiredRole: 'super_admin' },
  },
  organizations: {
    component: <OrganizationPage />,
    guardConfig: { requiredRole: 'super_admin' },
  },
  plan: {
    component: <PlanMasterPage />,
    guardConfig: { requiredRole: 'super_admin' },
  },
  storage: {
    component: <StorageMasterPage />,
    guardConfig: { requiredRole: 'super_admin' },
  },
  template_requests: {
    component: <TemplateRequestsPage />,
    guardConfig: { requiredRole: 'super_admin' },
  },

  // Projects
  projects: {
    component: <ProjectsPage />,
    guardConfig: {},
  },
  project_assign: {
    component: <ProjectAssignPage />,
    guardConfig: {},
  },
  data_collection: {
    component: <DataCollectionPage />,
    guardConfig: {},
  },

  // Chat
  chat: {
    component: <ChatViewPage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },

  // FieldStudio
  document_creator: {
    component: <DocumentCreatorListPage />,
    guardConfig: { excludeRoles: ['super_admin'] },
  },
  fieldstudio_templates: {
    component: <DocumentTemplateManagerPage />,
    guardConfig: { requiredRole: 'super_admin' },
  },

  // Hiring Management
  job_openings: {
    component: <HiringJobsPage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },
  applications: {
    component: <HiringApplicationsPage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },
  applications_hiring: {
    component: <HiringApplicationsPage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },
  pipeline: {
    component: <HiringPipelinePage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },
  interviews: {
    component: <HiringInterviewsPage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },
  hiring_settings: {
    component: <HiringSettingsPage />,
    guardConfig: { excludeRoles: ['super_admin'], skipRightsCheck: true },
  },
};

function DynamicRouteResolver() {
  const { moduleAlias, menuAlias, submenuAlias } = useParams();

  let key = submenuAlias || menuAlias;

  // Resolve generic list submenus to the parent menu list page
  if (submenuAlias === 'list') {
    key = menuAlias;
  }

  // Resolve naming collision on templates
  if (key === 'templates') {
    if (menuAlias === 'document_creator' || moduleAlias === 'fieldstudio') {
      key = 'fieldstudio_templates';
    } else {
      key = 'whatsapp_templates';
    }
  }

  let config = ROUTE_MAP[key];

  if (!config && key) {
    const normalizedKey = key.replace(/-/g, '_');
    config = ROUTE_MAP[normalizedKey];
  }

  if (!config) {
    return <Navigate to="/404" replace />;
  }

  const { component, guardConfig } = config;

  if (guardConfig?.noGuard) {
    return component;
  }

  return (
    <RightsGuard module={moduleAlias} menu={menuAlias} submenu={submenuAlias} {...guardConfig}>
      {component}
    </RightsGuard>
  );
}

export const dashboardRoutes = [
  // OVERVIEW MODULE - /overview/*
  // =====================================================
  {
    path: 'overview',
    element: DashboardWrapper,
    children: [
      { element: <IndexPage />, index: true },
      { path: 'dashboard', element: <IndexPage /> },
      { path: 'menu-structure', element: <MenuStructurePage /> },
    ],
  },

  // =====================================================
  // HRM MANAGEMENT MODULE - /hrm_management/*
  // =====================================================
  {
    path: 'hrm_management',
    element: DashboardWrapper,
    children: [
      // Direct link menu: Attendance (no submenu)
      {
        path: 'attendance',
        element: (
          <RightsGuard module="hrm_management" menu="attendance" skipRightsCheck>
            <AttendanceView />
          </RightsGuard>
        ),
      },
      {
        path: 'forget_punch',
        element: (
          <RightsGuard
            module="hrm_management"
            menu="forget_punch"
            skipRightsCheck
            featureFlag="forgetAllowanceEnabled"
          >
            <ForgetPunchRequestPage />
          </RightsGuard>
        ),
      },
      {
        path: 'user_management',
        children: [
          {
            element: (
              <RightsGuard module="hrm_management" menu="user_management" submenu="user_list">
                <UserPage />
              </RightsGuard>
            ),
            index: true,
          },
          {
            path: 'designation',
            element: (
              <RightsGuard module="hrm_management" menu="user_management" submenu="designation">
                <DesignationMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'department',
            element: (
              <RightsGuard module="hrm_management" menu="user_management" submenu="department">
                <DepartmentMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'users',
            element: (
              <RightsGuard module="hrm_management" menu="user_management" submenu="users">
                <UserPage />
              </RightsGuard>
            ),
          },
          {
            path: 'user_groups',
            element: (
              <RightsGuard module="hrm_management" menu="user_management" submenu="user_groups">
                <UserGroupPage />
              </RightsGuard>
            ),
          },
          {
            path: 'user_rights',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="user_management"
                submenu="user_rights"
                feature="view_all"
              >
                <UserRightsPage />
              </RightsGuard>
            ),
          },
          {
            path: 'attendance_approval',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="attendance"
                submenu="attendance_approval"
                feature="view_all"
              >
                <AttendanceApprovalView />
              </RightsGuard>
            ),
          },
          {
            path: 'forget_punch_approval',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="attendance"
                submenu="forget_punch_approval"
                feature="view_all"
                featureFlag="forgetAllowanceEnabled"
              >
                <ForgetPunchApprovalPage />
              </RightsGuard>
            ),
          },
          {
            path: 'geography_rights',
            element: (
              <RightsGuard requiredRole="admin">
                <GeographyUserRightsView />
              </RightsGuard>
            ),
          },
          {
            path: 'resignation_approval',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="resignation"
                submenu="resignation_approval"
                feature="view_all"
              >
                <ResignationApprovalPage />
              </RightsGuard>
            ),
          },
          {
            path: 'staff_migrations',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="user_management"
                submenu="staff_migrations"
                feature="view_all"
              >
                <StaffMigrationPage />
              </RightsGuard>
            ),
          },
          {
            path: 'reporting_hierarchy',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="user_management"
                submenu="reporting_hierarchy"
              >
                <ReportingHierarchyPage />
              </RightsGuard>
            ),
          },
          // Unified Calendar (direct link menu)
          {
            path: 'calendar',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="unifiedCalendar"
                excludeRoles={['super_admin']}
                skipRightsCheck
              >
                <UnifiedCalendarPage />
              </RightsGuard>
            ),
          },
          {
            path: 'staff-migrations',
            element: (
              <RightsGuard
                module="hrm_management"
                menu="user_management"
                submenu="staff_migrations"
                feature="view_all"
              >
                <StaffMigrationPage />
              </RightsGuard>
            ),
          },
          {
            path: 'holiday',
            element: (
              <RightsGuard module="hrm_management" menu="user_management" submenu="holiday">
                <HolidayCalendarPage />
              </RightsGuard>
            ),
          },
        ],
      },
      // Holiday Calendar Route (Simple Read-Only - Default Accessible)
      {
        path: 'holiday_calendar',
        element: (
          <RightsGuard module="hrm_management" menu="holiday_calendar" skipRightsCheck>
            <HolidaySimplePage />
          </RightsGuard>
        ),
      },
      // Holiday Management Route
      {
        path: 'holiday',
        element: (
          <RightsGuard module="hrm_management" menu="user_management" submenu="holiday">
            <HolidayCalendarPage />
          </RightsGuard>
        ),
      },
      {
        path: 'holiday_managment',
        element: (
          <RightsGuard module="hrm_management" menu="holiday_managment">
            <HolidayCalendarPage />
          </RightsGuard>
        ),
      },
      {
        path: 'holiday_management',
        element: (
          <RightsGuard module="hrm_management" menu="holiday_management">
            <HolidayCalendarPage />
          </RightsGuard>
        ),
      },
      // Daily Planner (direct link menu)
      {
        path: 'planner',
        element: (
          <RightsGuard module="hrm_management" menu="planner" excludeRoles={['super_admin']}>
            <DailyPlannerPage />
          </RightsGuard>
        ),
      },
      // Geo Fencing (direct link menu)
      {
        path: 'geo_fencing',
        element: (
          <RightsGuard module="hrm_management" menu="geo_fencing" excludeRoles={['super_admin']}>
            <GeoFencingPage />
          </RightsGuard>
        ),
      },
      // Resignation (self-service — all org users)
      {
        path: 'resignation',
        element: (
          <RightsGuard module="hrm_management" menu="resignation" skipRightsCheck>
            <ResignationListPage />
          </RightsGuard>
        ),
      },
      // Document Type Master (admin manages types)
    ],
  },

  // =====================================================
  // DOCUMENTS MODULE - /documents/*
  // =====================================================
  {
    path: 'documents',
    element: DashboardWrapper,
    children: [
      // Document Type Master (admin manages types)
      {
        path: 'document_type',
        element: (
          <RightsGuard module="documents" menu="document_type">
            <DocumentTypeMasterPage />
          </RightsGuard>
        ),
      },
      // My Document Requests (all org users)
      {
        path: 'document_requests',
        element: (
          <RightsGuard module="documents" menu="document_requests" skipRightsCheck>
            <DocumentRequestPage />
          </RightsGuard>
        ),
      },
      // Document Request Approvals (admin + reporting manager)
      {
        path: 'document_request_approvals',
        element: (
          <RightsGuard module="documents" menu="document_request_approvals">
            <DocumentRequestApprovalPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // KIOSK MANAGEMENT MODULE - /kiosk/*
  // =====================================================
  {
    path: 'kiosk',
    element: DashboardWrapper,
    children: [
      {
        path: 'list',
        element: (
          <RightsGuard skipRightsCheck>
            <KioskListPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // LEAVE MANAGEMENT MODULE - /leave_mangement/*
  // =====================================================
  {
    path: 'leave_mangement',
    element: DashboardWrapper,
    children: [
      {
        path: 'leave_types',
        element: (
          <RightsGuard module="leave_mangement" menu="leave_types">
            <LeaveTypeMasterPage />
          </RightsGuard>
        ),
      },
      {
        path: 'leave_applications',
        element: (
          <RightsGuard module="leave_mangement" menu="leave_applications" skipRightsCheck>
            <LeaveApplicationPage />
          </RightsGuard>
        ),
      },
      {
        path: 'leave_approvals',
        element: (
          <RightsGuard module="leave_mangement" menu="leave_approvals">
            <LeaveApprovalPage />
          </RightsGuard>
        ),
      },
      {
        path: 'leave_analytics',
        element: (
          <RightsGuard module="leave_mangement" menu="leave_approvals">
            <LeaveAnalyticsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'leave_encashment',
        element: (
          <RightsGuard module="leave_mangement" menu="leave_applications" skipRightsCheck>
            <LeaveEncashmentPage />
          </RightsGuard>
        ),
      },
      {
        path: 'leave_balances',
        element: (
          <RightsGuard module="leave_mangement" menu="leave_balances">
            <LeaveBalancePage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // PAYROLL MODULE - /payroll_management/*
  // =====================================================
  {
    path: 'payroll_management',
    element: DashboardWrapper,
    children: [
      {
        path: 'payroll',
        element: <PayrollListView />,
      },
      {
        path: 'payroll/:id',
        element: <PayrollDetailView />,
      },
      {
        path: 'my_payslips',
        element: <MyPayslipsView />,
      },
      {
        path: 'employee_salary',
        element: <EmployeeSalaryListView />,
      },
    ],
  },

  // =====================================================
  // REPORTS MODULE - /reports/*
  // =====================================================
  {
    path: 'reports',
    element: DashboardWrapper,
    children: [
      {
        path: 'monthly_attendance',
        element: (
          <RightsGuard module="reports" menu="monthly_attendance" excludeRoles={['super_admin']}>
            <MonthlyAttendanceReportsView />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // MASTER MODULE - /master/*
  // =====================================================
  {
    path: 'master',
    element: DashboardWrapper,
    children: [
      // Jurisdiction submenu
      {
        path: 'jurisdiction',
        children: [
          { element: <CountryMasterPage />, index: true },
          {
            path: 'country',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="country">
                <CountryMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'state',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="state">
                <StateMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'city',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="city">
                <CityMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'block',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="block">
                <BlockMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'locality',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="locality">
                <LocalityMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'cluster',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="cluster">
                <ClusterMasterPage />
              </RightsGuard>
            ),
          },
        ],
      },
      // Menu Management submenu
      {
        path: 'menu_mangement',
        children: [
          { element: <ModuleMasterPage />, index: true },
          {
            path: 'module',
            element: (
              <RightsGuard module="master" menu="menu_mangement" submenu="module_master">
                <ModuleMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'menu_master',
            element: (
              <RightsGuard module="master" menu="menu_mangement" submenu="menu_master">
                <MenuMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'submenu',
            element: (
              <RightsGuard module="master" menu="menu_mangement" submenu="submenu_master">
                <SubmenuMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'feature',
            element: (
              <RightsGuard module="master" menu="menu_mangement" submenu="feature_master">
                <FeatureMasterPage />
              </RightsGuard>
            ),
          },
        ],
      },
    ],
  },

  // =====================================================
  // SETTINGS MODULE - /settings/*
  // =====================================================
  {
    path: 'settings',
    element: DashboardWrapper,
    children: [
      {
        path: 'site_settings',
        element: (
          <RightsGuard module="settings" menu="site_settings">
            <SiteSettingsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'whatsapp_logs',
        element: (
          <RightsGuard module="settings" menu="whatsapp_logs" skipRightsCheck>
            <WhatsAppLogPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // WHATSAPP MODULE - /whatsapp/*
  // =====================================================
  {
    path: 'whatsapp',
    element: DashboardWrapper,
    children: [
      {
        path: 'analytics',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppAnalyticsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'inbox',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppInboxPage />
          </RightsGuard>
        ),
      },
      {
        path: 'campaigns',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppCampaignsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'templates',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppTemplatesPage />
          </RightsGuard>
        ),
      },
      {
        path: 'sequences',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppSequencesPage />
          </RightsGuard>
        ),
      },
      {
        path: 'bot-faqs',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppBotFAQPage />
          </RightsGuard>
        ),
      },
      {
        path: 'logs',
        element: (
          <RightsGuard module="whatsapp" menu="logs" skipRightsCheck>
            <WhatsAppLogPage />
          </RightsGuard>
        ),
      },
      {
        path: 'contacts',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppContactsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'knowledge-base',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppKnowledgePage />
          </RightsGuard>
        ),
      },
      {
        path: 'flows',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppFlowsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'flows/:id/builder',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppFlowBuilderPage />
          </RightsGuard>
        ),
      },
      {
        path: 'bot-flow-manager',
        element: (
          <RightsGuard requiredRole="super_admin" skipRightsCheck>
            <WhatsAppBotFlowManagerPage />
          </RightsGuard>
        ),
      },
      {
        path: 'bot_manager',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppOrgBotManagerPage />
          </RightsGuard>
        ),
      },
      {
        path: 'settings',
        element: (
          <RightsGuard skipRightsCheck>
            <WhatsAppSettingsPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // FESTIVAL MODULE - /festival/*
  // =====================================================
  {
    path: 'festivals',
    element: DashboardWrapper,
    children: [
      {
        path: 'festival',
        element: (
          <RightsGuard module="festivals" menu="festival">
            <FestivalMasterPage />
          </RightsGuard>
        ),
      },
    ],
  },
  // =====================================================
  // EXPENSE CATEGORY MODULE - /expense_category
  // =====================================================
  {
    path: 'expense',
    element: DashboardWrapper,
    children: [
      {
        path: 'expense_category',
        element: (
          <RightsGuard module="expense" menu="expense_category" featureFlag="expenseFeatureEnabled">
            <ExpenseCategoryPage />
          </RightsGuard>
        ),
      },
      {
        path: 'manage_expense',
        element: (
          <RightsGuard module="expense" menu="manage_expense" featureFlag="expenseFeatureEnabled">
            <ManageExpensePage />
          </RightsGuard>
        ),
      },
      {
        path: 'expense_approvals',
        element: (
          <RightsGuard module="expense" menu="expense_approvals" featureFlag="expenseFeatureEnabled">
            <ExpenseApprovalPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // SUPER ADMIN MODULE - /super_admin/*
  // =====================================================
  {
    path: 'super_admin',
    element: DashboardWrapper,
    children: [
      {
        path: 'admin_users',
        element: (
          <RightsGuard requiredRole="super_admin">
            <AdminUserPage />
          </RightsGuard>
        ),
      },
      {
        path: 'organizations',
        element: (
          <RightsGuard requiredRole="super_admin">
            <OrganizationPage />
          </RightsGuard>
        ),
      },
      {
        path: 'plan_storage',
        element: (
          <RightsGuard requiredRole="super_admin">
            <Outlet />
          </RightsGuard>
        ),
        children: [
          {
            element: <PlanMasterPage />,
            index: true,
          },
          {
            path: 'plan',
            element: <PlanMasterPage />,
          },
          {
            path: 'storage',
            element: <StorageMasterPage />,
          },
        ],
      },
      {
        path: 'template_requests',
        element: (
          <RightsGuard requiredRole="super_admin">
            <TemplateRequestsPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // PROJECT MODULE - /projects/*
  // =====================================================
  {
    path: 'project_management',
    element: DashboardWrapper,
    children: [
      {
        path: 'projects',
        element: (
          <RightsGuard module="project_management" menu="projects">
            <ProjectsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'project_assign',
        element: (
          <RightsGuard module="project_management" menu="project_assign">
            <ProjectAssignPage />
          </RightsGuard>
        ),
      },
      {
        path: 'data_collection',
        children: [
          {
            index: true,
            element: <DataCollectionPage />,
          },
          {
            path: 'forms',
            element: <ProjectFormsPage />,
          },
          {
            path: 'forms/:id/data',
            element: <FormDataListPage />,
          },
          {
            path: 'forms/create',
            element: <FormBuilderPage />,
          },
          {
            path: 'forms/:id/edit',
            element: <FormBuilderPage />,
          },
          {
            path: 'forms/:id/submit',
            element: <FormSubmissionPage />,
          },
        ],
      },
    ],
  },

  // =====================================================
  // USER PROFILE - /dashboard/user/*
  // =====================================================
  {
    path: 'dashboard/user',
    element: DashboardWrapper,
    children: [
      { element: <UserProfilePage />, index: true },
      { path: 'profile', element: <UserProfilePage /> },
      { path: 'cards', element: <UserCardsPage /> },
      { path: 'list', element: <UserListPage /> },
    ],
  },

  // =====================================================
  // LEGACY /dashboard ROUTES (for backward compatibility)
  // =====================================================
  {
    path: 'dashboard',
    element: DashboardWrapper,
    children: [
      { element: <IndexPage />, index: true },
      { path: 'onboarding', element: <OnboardingPage /> },
      {
        path: 'admin-users',
        element: (
          <RightsGuard requiredRole="super_admin">
            <AdminUserPage />
          </RightsGuard>
        ),
      },
      {
        path: 'users',
        element: (
          <RightsGuard module="hrm_management" menu="user_management" submenu="user_list">
            <UserPage />
          </RightsGuard>
        ),
      },
      {
        path: 'projects',
        element: (
          <RightsGuard module="project_management" menu="projects">
            <ProjectsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'app-users',
        element: (
          <RightsGuard module="hrm_management" menu="user_management" submenu="app_users">
            <AppUserPage />
          </RightsGuard>
        ),
      },
      {
        path: 'organizations',
        element: (
          <RightsGuard requiredRole="super_admin">
            <OrganizationPage />
          </RightsGuard>
        ),
      },

      {
        path: 'site-settings',
        element: (
          <RightsGuard module="settings" menu="site_settings">
            <SiteSettingsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'module-master',
        element: (
          <RightsGuard module="master" menu="menu_mangement" submenu="module_master">
            <ModuleMasterPage />
          </RightsGuard>
        ),
      },
      {
        path: 'menu-master',
        element: (
          <RightsGuard module="master" menu="menu_mangement" submenu="menu_master">
            <MenuMasterPage />
          </RightsGuard>
        ),
      },
      {
        path: 'submenu-master',
        element: (
          <RightsGuard module="master" menu="menu_mangement" submenu="submenu_master">
            <SubmenuMasterPage />
          </RightsGuard>
        ),
      },
      {
        path: 'designations',
        element: (
          <RightsGuard module="hrm_management" menu="user_management" submenu="designation">
            <DesignationMasterPage />
          </RightsGuard>
        ),
      },
      {
        path: 'staff-migrations',
        element: (
          <RightsGuard
            module="hrm_management"
            menu="user_management"
            submenu="staff_migrations"
            feature="view_all"
          >
            <StaffMigrationPage />
          </RightsGuard>
        ),
      },
      {
        path: 'staff_migrations',
        element: (
          <RightsGuard
            module="hrm_management"
            menu="user_management"
            submenu="staff_migrations"
            feature="view_all"
          >
            <StaffMigrationPage />
          </RightsGuard>
        ),
      },
      {
        path: 'leave-types',
        element: (
          <RightsGuard module="leave_mangement" menu="leave_types">
            <LeaveTypeMasterPage />
          </RightsGuard>
        ),
      },
      {
        path: 'jurisdiction',
        children: [
          { element: <CountryMasterPage />, index: true },
          {
            path: 'country',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="country">
                <CountryMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'state',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="state">
                <StateMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'cities',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="city">
                <CityMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'blocks',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="block">
                <BlockMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'localities',
            element: (
              <RightsGuard module="master" menu="jurisdiction" submenu="locality">
                <LocalityMasterPage />
              </RightsGuard>
            ),
          },
          {
            path: 'holidays',
            element: (
              <RightsGuard module="hrm_management" menu="holiday_calendar" skipRightsCheck>
                <HolidayCalendarPage />
              </RightsGuard>
            ),
          },
        ],
      },
      {
        path: 'drive',
        element: (
          <RightsGuard module="storage" menu="drive" skipRightsCheck>
            <DriveManager />
          </RightsGuard>
        ),
      },
      {
        path: 'chat',
        element: (
          <RightsGuard module="chat" menu="chat" excludeRoles={['super_admin']} skipRightsCheck>
            <ChatViewPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // TRAVEL MANAGEMENT MODULE - /travel_management/*
  // =====================================================
  {
    path: 'travel_management',
    element: DashboardWrapper,
    children: [
      {
        path: 'vehicle_types',
        element: (
          <RightsGuard
            module="travel_management"
            menu="vehicle_types"
            excludeRoles={['super_admin']}
          >
            <VehicleTypeMasterPage />
          </RightsGuard>
        ),
      },
      {
        path: 'vehical_milege',
        element: (
          <RightsGuard
            module="travel_management"
            menu="vehical_milege"
            excludeRoles={['super_admin']}
          >
            <VehicleMileagePage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // ANNOUNCEMENT MODULE - /announcement/*
  // =====================================================
  {
    path: 'hrm_management',
    element: DashboardWrapper,
    children: [
      {
        path: 'announcement',
        element: (
          <RightsGuard module="announcement" menu="announcement" excludeRoles={['super_admin']}>
            <AnnouncementPage />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // CHAT MODULE - /chat/*
  // =====================================================
  {
    path: 'chat',
    element: DashboardWrapper,
    children: [
      {
        path: 'chat',
        element: (
          <RightsGuard module="chat" menu="chat" excludeRoles={['super_admin']} skipRightsCheck>
            <ChatViewPage />
          </RightsGuard>
        ),
      },
    ],
  },
  // =====================================================
  // STORAGE MODULE - /storage/*
  // =====================================================
  {
    path: 'storage',
    element: DashboardWrapper,
    children: [
      {
        index: true,
        element: (
          <RightsGuard module="storage" menu="storage_overview" excludeRoles={['super_admin']}>
            <StorageDashboardView />
          </RightsGuard>
        ),
      },
      {
        path: 'storage_overview',
        element: (
          <RightsGuard module="storage" menu="storage_overview" excludeRoles={['super_admin']}>
            <StorageDashboardView />
          </RightsGuard>
        ),
      },
      {
        path: 'drive',
        element: (
          <RightsGuard module="storage" menu="drive" skipRightsCheck>
            <DriveManager />
          </RightsGuard>
        ),
      },
    ],
  },

  // =====================================================
  // ANALYTICS DASHBOARD MODULE - /dashboard/analytics-dashboard/*
  // Viewer inside dashboard layout
  // =====================================================
  {
    path: 'dashboard',
    element: DashboardWrapper,
    children: [
      {
        path: 'analytics-dashboard',
        element: (
          <RightsGuard excludeRoles={['super_admin']} featureFlag="customDashboards">
            <AnalyticsDashboardListPage />
          </RightsGuard>
        ),
      },
      {
        path: 'analytics-dashboard/:id',
        element: (
          <RightsGuard excludeRoles={['super_admin']} featureFlag="customDashboards">
            <DashboardDesignerPage viewMode />
          </RightsGuard>
        ),
      },
    ],
  },
  // Designer routes — STANDALONE (no dashboard layout) for full-screen editing
  {
    path: 'dashboard/analytics-dashboard/create',
    element: (
      <AuthGuard>
        <RightsGuard excludeRoles={['super_admin']} featureFlag="customDashboards">
          <Suspense fallback={<LoadingScreen />}>
            <DashboardDesignerPage />
          </Suspense>
        </RightsGuard>
      </AuthGuard>
    ),
  },
  {
    path: 'dashboard/analytics-dashboard/:id/edit',
    element: (
      <AuthGuard>
        <RightsGuard excludeRoles={['super_admin']} featureFlag="customDashboards">
          <Suspense fallback={<LoadingScreen />}>
            <DashboardDesignerPage />
          </Suspense>
        </RightsGuard>
      </AuthGuard>
    ),
  },

  // =====================================================
  // WEBSITE BUILDER MODULE
  // List + Analytics inside dashboard layout
  // =====================================================
  {
    path: 'dashboard',
    element: DashboardWrapper,
    children: [
      {
        path: 'website-builder',
        element: (
          <RightsGuard module="website_builder" menu="website_list" excludeRoles={['super_admin']}>
            <WebsiteListPage />
          </RightsGuard>
        ),
      },
      {
        path: 'website-builder/:id/analytics',
        element: (
          <RightsGuard module="website_builder" menu="website_list" excludeRoles={['super_admin']}>
            <WebsiteAnalyticsPage />
          </RightsGuard>
        ),
      },
    ],
  },
  // Editor route — STANDALONE (no dashboard layout) for full-screen editing
  {
    path: 'dashboard/website-builder/:id/edit',
    element: (
      <AuthGuard>
        <RightsGuard module="website_builder" menu="website_list" excludeRoles={['super_admin']}>
          <Suspense fallback={<LoadingScreen />}>
            <WebsiteEditorPage />
          </Suspense>
        </RightsGuard>
      </AuthGuard>
    ),
  },

  // =====================================================
  // FIELDSTUDIO MODULE — /fieldstudio/*
  // List page inside dashboard layout; editor is standalone full-screen
  // =====================================================
  {
    path: 'fieldstudio',
    element: DashboardWrapper,
    children: [
      {
        path: 'document_creator',
        element: (
          <RightsGuard module="fieldstudio" menu="document_creator" excludeRoles={['super_admin']}>
            <DocumentCreatorListPage />
          </RightsGuard>
        ),
      },
      {
        path: 'document_creator/templates',
        element: (
          <RightsGuard requiredRole="super_admin">
            <DocumentTemplateManagerPage />
          </RightsGuard>
        ),
      },

      {
        path: 'templates',
        element: (
          <RightsGuard module="fieldstudio" menu="document_creator" excludeRoles={['super_admin']}>
            <TemplateGalleryPage />
          </RightsGuard>
        ),
      },
      {
        path: 'marketplace',
        element: (
          <RightsGuard module="fieldstudio" menu="document_creator" excludeRoles={['super_admin']}>
            <TemplateMarketplacePage />
          </RightsGuard>
        ),
      },
    ],
  },
  // FieldStudio Editor — STANDALONE full-screen (no sidebar)
  {
    path: 'fieldstudio/document_creator/create',
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <DocumentEditorPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: 'fieldstudio/document_creator/:id/edit',
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <DocumentEditorPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  // FieldStudio Template Editor - loads template and redirects to document editor
  {
    path: 'fieldstudio/editor/:templateId',
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <TemplateToEditorLoader />
        </Suspense>
      </AuthGuard>
    ),
  },

  // Form Analytics Studio — STANDALONE full-screen (no sidebar)
  {
    path: 'project_management/data_collection/forms/:id/analytics',
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <FormAnalyticsPage />
        </Suspense>
      </AuthGuard>
    ),
  },

  // =====================================================
  // DYNAMIC ROUTES RESOLVER (Fallback for moved/renamed menus)
  // =====================================================
  {
    path: ':moduleAlias/:menuAlias',
    element: DashboardWrapper,
    children: [
      {
        path: '',
        element: <DynamicRouteResolver />,
      },
    ],
  },
  {
    path: ':moduleAlias/:menuAlias/:submenuAlias',
    element: DashboardWrapper,
    children: [
      {
        path: '',
        element: <DynamicRouteResolver />,
      },
    ],
  },
  // =====================================================
  // HIRING MANAGEMENT MODULE - /hiring_management/*
  // Admin only — super_admin is blocked at both nav and route level
  // =====================================================
  {
    path: 'hiring_management',
    element: DashboardWrapper,
    children: [
      {
        path: 'job_openings',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="job_openings"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringJobsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'applications_hiring',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="applications_hiring"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringApplicationsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'pipeline',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="pipeline"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringPipelinePage />
          </RightsGuard>
        ),
      },
      {
        path: 'interviews',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="interviews"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringInterviewsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'hiring_settings',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="hiring_settings"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringSettingsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'analytics',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="analytics"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringAnalyticsPage />
          </RightsGuard>
        ),
      },
      {
        path: 'talent_pool',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="talent_pool"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringTalentPoolPage />
          </RightsGuard>
        ),
      },
      {
        path: 'referrals',
        element: (
          <RightsGuard
            module="hiring_management"
            menu="referrals"
            excludeRoles={['super_admin']}
            skipRightsCheck
          >
            <HiringReferralsPage />
          </RightsGuard>
        ),
      },
    ],
  },
  // =====================================================
  // 404 CATCH-ALL (Should be very last)
  // =====================================================
  // { path: '404', element: <Page404 /> },
  // { path: '*', element: <Navigate to="/404" replace /> },
];
